// useStudentNotifications — loads + streams notifications for the logged-in student | drives the notification bell badge count

import { useEffect, useState, useCallback } from 'react'
import { UserProfile } from '../context/ProfileContext'
import {
  getStudentNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  subscribeToNotifications,
  type StudentNotification,
} from '../services/notificationsApi'

export function useStudentNotifications() {
  const profileContext = UserProfile()
  // Use students.id (internal PK), NOT session.user.id (auth UUID) — they differ
  const studentId = profileContext?.profile?.id ?? null

  const [notifications, setNotifications] = useState<StudentNotification[]>([])
  const [loading, setLoading] = useState(false)

  const unreadCount = notifications.filter((n) => !n.is_read).length

  // Initial load
  useEffect(() => {
    if (!studentId) return
    setLoading(true)
    getStudentNotifications(studentId)
      .then(setNotifications)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [studentId])

  // Realtime: prepend new notifications as they arrive
  useEffect(() => {
    if (!studentId) return
    const unsubscribe = subscribeToNotifications(studentId, (newNotif) => {
      setNotifications((prev) => [newNotif, ...prev])
    })
    return () => { unsubscribe() }
  }, [studentId])

  const markRead = useCallback(async (notificationId: string) => {
    await markNotificationRead(notificationId)
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
      ),
    )
  }, [])

  const markAllRead = useCallback(async () => {
    if (!studentId) return
    await markAllNotificationsRead(studentId)
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })),
    )
  }, [studentId])

  return { notifications, unreadCount, loading, markRead, markAllRead }
}
